import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import { green, yellow } from "@mui/material/colors";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { Map } from "schemas/functions";

import Editor from "components/Editor";
import { useMergeTags } from "components/Editor/MergeTag/useMergeTags";
import { extensions } from "components/Editor/utils";
import { MERGE_TAGS } from "constants/templates";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import NoMergeTagsWarningDialog from "features/Influencer/Templates/TemplatesTable/NoMergeTagsWarningDialog";
import { fetcherAuth } from "utils/api";
import { containsMergeTagsTemplates } from "utils/string";

import DraftLoader from "../EmailBrandForm/DraftLoader";
import styles from "../styles";

interface TemplateFormProps {
  handleBack: () => void;
  handleNext: (onboardingForm?: Map) => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
}

const TemplateForm = ({
  handleBack,
  handleNext,
  onboardingForm,
  setOnboardingForm,
}: TemplateFormProps) => {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [draftTemplate, setDraftTemplate] = useState<Template | null>(null);
  const [toggleMergeTagTooltip, toggleVariableTooltip] = useMergeTags();
  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);
  const [openMergeTagWarning, setOpenMergeTagWarning] =
    useState<boolean>(false);

  const subjectEditor = useEditor(
    {
      extensions: extensions({
        isPlain: true,
        suggestionOptions: MERGE_TAGS,
        placeholderText: "Collaborating with a Beauty Influencer",
      }),
      content: draftTemplate?.subject,
      onUpdate({ editor }) {
        setDraftTemplate((prevTemplate: Template | null) => {
          if (!prevTemplate) {
            return null;
          }
          return { ...prevTemplate, subject: editor.getHTML() };
        });
      },
    },
    [!!draftTemplate?.subject],
  ) as TipTapEditor;

  const draftTemplateEditor = useEditor(
    {
      extensions: extensions({
        toggleVariableTooltip,
        toggleMergeTagTooltip,
        suggestionOptions: MERGE_TAGS,
        placeholderText:
          "Hi there, I'm a fan of your brand's work in the industry, and I'm reaching out to see if you're interested in a collaboration...",
      }),
      content: draftTemplate?.body,
      onUpdate({ editor }) {
        setDraftTemplate((prevTemplate: Template | null) => {
          if (!prevTemplate) {
            return null;
          }
          return { ...prevTemplate, body: editor.getHTML() };
        });
      },
    },
    [!!draftTemplate?.body],
  ) as TipTapEditor;

  const signatureEditor = useEditor(
    {
      extensions: extensions({
        placeholderText: "Best, {Your Name}",
        hardBreaksOnly: true,
      }),
      content: draftTemplate?.signature,
      onUpdate({ editor }) {
        setDraftTemplate((prevTemplate: Template | null) => {
          if (!prevTemplate) {
            return null;
          }
          return { ...prevTemplate, signature: editor.getHTML() };
        });
        setOnboardingForm((prev) => {
          return { ...prev, signature: editor.getHTML() };
        });
      },
    },
    [!!draftTemplate?.signature],
  ) as TipTapEditor;

  const generateTemplate = async () => {
    if (!currentOrg) {
      return;
    }
    setLoading(true);
    try {
      const { subject, body, signature } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/draft`,
        "POST",
        {},
        onboardingForm.hasPitchEmail
          ? {
              pitchEmail: onboardingForm.pitchEmail,
              pitchSignature: onboardingForm.signature,
            }
          : {
              standsOutDescription: onboardingForm.standsOutDescription,
            },
      );
      if (!body) {
        setAlert(
          "Unable to generate template. Please reload and try again",
          "error",
        );
        return;
      }
      setDraftTemplate({
        name: "",
        subject,
        body,
        signature,
        contentType: TemplateContentType.OUTREACH,
      });
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (!currentOrg) {
      return;
    }

    if (!containsMergeTagsTemplates(draftTemplate?.body || "")) {
      setOpenMergeTagWarning(true);

      return;
    }

    createTemplate();
  };

  const createTemplate = async () => {
    setButtonLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/from-onboarding`,
        "POST",
        {},
        {
          ...draftTemplate,
        },
      );
      handleNext();
      fetchOutreachTemplates();
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save this template. Please reload and try again",
        "error",
      );
    } finally {
      setButtonLoading(false);
    }
  };

  useEffect(() => {
    generateTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center" }}>
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-paper-plane"
        />
        How about this for a pitch template?
      </Typography>
      {!loading && (
        <>
          <Typography>
            Make any edits, then click 'Create Template' and we'll save it to
            your dashboard. You can use this template to email brands faster and
            easier. Things to keep in mind before saving:
          </Typography>
          <ul>
            <li style={{ listStyleType: "none" }}>
              <Typography>
                <Box
                  component="i"
                  className="fa-solid fa-pen-nib"
                  sx={{ pr: 1, color: green[700] }}
                />
                Review the tone, details, and add anything you'd like before
                saving.
              </Typography>
            </li>
            <li style={{ listStyleType: "none" }}>
              <Typography>
                <Box
                  component="i"
                  className="fa-solid fa-sparkles"
                  sx={{ pr: 1, color: yellow[700] }}
                />
                Keep {"{Brand Name}"} and {"{First Name}"} as placeholders—this
                ensures each email is personalized for the brand and recipient.
              </Typography>
            </li>
            <li style={{ listStyleType: "none" }}>
              <Typography>
                <Box
                  component="i"
                  className="fa-solid fa-file-pen"
                  sx={{ pr: 1, color: "primary.main" }}
                />
                At any time, you can modify or create new templates by clicking
                the dropdown button in the top right corner and selecting
                "Templates."
              </Typography>
            </li>
          </ul>
          <Divider sx={{ my: 3 }} />
        </>
      )}

      {loading && (
        <Box sx={styles.templateWrapper}>
          <Box sx={styles.loaderWrapper}>
            <DraftLoader />
          </Box>
        </Box>
      )}
      {!loading && (
        <Grid container justifyContent="flex-start" alignItems="flex-start">
          <Grid container item rowGap={1} xs={12}>
            <Grid item xs={12}>
              <FormHelperText sx={styles.helperText}>Subject</FormHelperText>
              <Editor editor={subjectEditor} hideMenu mergeTags={MERGE_TAGS} />
            </Grid>
            <Grid item xs={12}>
              <Editor
                editor={draftTemplateEditor}
                mergeTags={MERGE_TAGS}
                allowImage={true}
                allowAttachment={true}
                openAttachmentManager={() => {
                  setAttachmentDialogOpen(true);
                }}
                attachment={draftTemplate?.attachment}
                removeAttachment={() => {
                  setDraftTemplate((prevTemplate: Template | null) => {
                    if (!prevTemplate) return null;
                    return { ...prevTemplate, attachment: undefined };
                  });
                }}
              />
            </Grid>
            {draftTemplate?.signature && (
              <Grid item xs={12}>
                <FormHelperText sx={styles.helperText}>
                  Signature
                </FormHelperText>
                <Editor editor={signatureEditor} />
              </Grid>
            )}
            <AttachmentManager
              enteredSelection={(attachment: string) => {
                setDraftTemplate((prevTemplate: Template | null) => {
                  if (!prevTemplate) return null;
                  return { ...prevTemplate, attachment };
                });
              }}
              open={attachmentDialogOpen}
              setOpen={setAttachmentDialogOpen}
            />
            <Grid
              item
              xs={12}
              container
              gap={1}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              <Grid item xs="auto">
                <Button onClick={handleBack} color="secondary" disableElevation>
                  Back
                </Button>
              </Grid>
              <Grid item xs="auto">
                <LoadingButton
                  disableElevation
                  onClick={onSubmit}
                  variant="contained"
                  loading={buttonLoading}
                >
                  Create Template
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      <NoMergeTagsWarningDialog
        open={openMergeTagWarning}
        handleClose={() => setOpenMergeTagWarning(false)}
        onProceed={createTemplate}
      />
    </>
  );
};

export default TemplateForm;
